import React from "react";
import InfoSquare from "../../components/InfoSquare.jsx";
import { basisLabel, deltaParts, money, monthShort, paceForecast, pctText, sum } from "./derive.js";

function Cell({ kicker, info, value, note, noteClass }) {
  return (
    <div className="stat-cell">
      <div className="stat-kicker">
        {kicker}
        {info}
      </div>
      <div className="stat-big">{value}</div>
      {note && <div className={`stat-note${noteClass ? ` ${noteClass}` : ""}`}>{note}</div>}
    </div>
  );
}

function noteClassFor(delta) {
  if (!delta) return null;
  return delta.direction === "up" ? "stat-note-up" : delta.direction === "down" ? "stat-note-down" : null;
}

function signedPct(delta) {
  if (!delta) return "—";
  if (delta.pct === null) return "new";
  return `${delta.diff > 0 ? "+" : delta.diff < 0 ? "−" : "±"}${pctText(delta.pct)}`;
}

/**
 * The numbers that answer "are we on track" before any chart is read.
 *
 * These cells are always present — unlike the pane deltas, they are not
 * hidden by the compare toggle. "Same point last month" is the reference the
 * whole page is built around; a user who turns comparison off is asking for
 * the row-level noise to stop, not for the headline number to disappear.
 *
 * For a month in progress there are two forecast cells, and they are two
 * different claims rather than one number computed twice: ours projects the
 * chosen daily pace forward, AWS's comes from Cost Explorer's model and is
 * what the AWS console will show. When they disagree materially that is
 * itself the signal — usually a spike inside our averaging window, or a
 * commitment AWS knows about and a daily average cannot see.
 *
 * For a closed month both are gone: the forecast cell becomes the finished
 * month's change against the one before it, and there is nothing to project.
 */
export default function StatRow({ period, prevPeriod, daily, basis, openNote, onToggleNote }) {
  const series = (daily?.daily || []).map(Number);
  const previousSeries = (daily?.previous_daily || []).map(Number);
  const days = daily?.days_in_month || 0;
  const elapsed = series.length;
  const closed = days > 0 && elapsed >= days;
  const awsForecast = daily?.aws_forecast || null;

  const mtd = sum(series);
  const samePoint = sum(previousSeries.slice(0, elapsed));
  const previousTotal = sum(previousSeries);
  const pace = elapsed ? mtd / elapsed : 0;
  // Last month's pace over the SAME number of elapsed days, not its full-month
  // average — comparing 24 days of this month against 31 of last would flatter
  // whichever month happens to be further along.
  const previousPace = elapsed ? samePoint / elapsed : 0;

  const projection = paceForecast(series, days, basis);

  const cur = monthShort(period);
  const prev = monthShort(prevPeriod);

  const vsSamePoint = deltaParts(mtd, samePoint);
  const vsPrevTotal = deltaParts(closed ? mtd : projection.monthEnd, previousTotal);
  const awsVsPrev = awsForecast ? deltaParts(awsForecast.month_end, previousTotal) : null;

  return (
    <div className="stat-row">
      <Cell
        kicker={closed ? "Month total" : "Month to date"}
        value={money(mtd)}
        note={closed ? `1–${days} ${cur} · month closed` : `1–${elapsed} ${cur} · ${elapsed} of ${days} days`}
      />

      <Cell
        kicker={`Same point, ${prev}`}
        value={money(samePoint)}
        note={vsSamePoint ? `${vsSamePoint.amountText} (${vsSamePoint.pctLabel}) this period` : null}
        noteClass={noteClassFor(vsSamePoint)}
      />

      <Cell kicker="Daily pace" value={money(pace)} note={`${basisLabel(basis).toLowerCase()} avg ${money(projection.pace)} · ${prev} ${money(previousPace)}`} />

      {closed ? (
        <Cell
          kicker={`vs ${prev} total`}
          value={signedPct(vsPrevTotal)}
          note={`${money(previousTotal)} → ${money(mtd)}`}
          noteClass={noteClassFor(vsPrevTotal)}
        />
      ) : (
        <>
          <Cell
            kicker={`Forecast (${basisLabel(basis)})`}
            info={<InfoSquare id="forecast" openNote={openNote} onToggle={onToggleNote} label="How the forecast is calculated" />}
            value={money(projection.monthEnd)}
            note={`${signedPct(vsPrevTotal)} vs ${prev} (${money(previousTotal)})`}
            noteClass={noteClassFor(vsPrevTotal)}
          />

          {awsForecast && (
            <Cell
              kicker="AWS forecast"
              value={money(awsForecast.month_end)}
              note={
                awsForecast.month_end_lower !== null && awsForecast.month_end_upper !== null
                  ? `${awsForecast.interval}% range ${money(awsForecast.month_end_lower)}–${money(awsForecast.month_end_upper)}`
                  : awsVsPrev
                    ? `${signedPct(awsVsPrev)} vs ${prev}`
                    : null
              }
            />
          )}
        </>
      )}
    </div>
  );
}
